import React from 'react';
import { A } from '@osmosis/react-ui/components/Text';
import { H2 } from '@osmosis/react-ui/components/Text';
import { P } from '@osmosis/react-ui/components/Text';
import { FormattedMessage } from 'react-intl';
import { Box, Button, Flex, Image } from 'rebass';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { Plan, Deal } from '../../types/plans';
import { useIntl } from 'react-intl';
import { darken } from '@osmosis/shared/styles/colorModifiers';

import {
  getRenewalTitle,
  getTitle,
  trackPlanSelection,
  getCurrencySymbol,
  getSubtitle,
  useReducedPrice,
  formatPrice,
} from './utils';

/**
 * Plan Card.
 */
export const PlanCard = ({
  index,
  plan,
  deal,
}: {
  index: number;
  plan: Plan;
  deal?: Deal;
}) => {
  const [isActive, setIsActive] = React.useState(false);
  const colors = useColors();

  const intl = useIntl();
  const isDeal = !!(deal && Object.keys(deal).length > 0);
  const reducedPrice = useReducedPrice(isDeal, plan, deal);
  const title = getTitle(plan.intervalLength, intl);
  const subtitle = getSubtitle(index, intl);
  const renewalTitle = getRenewalTitle(plan.intervalLength);
  const textColor = index === 1 ? colors.white : colors.grey;

  const href = `/buy?plan=${plan.code}${deal ? `&deal=${deal.url}` : ''}`;

  const onPlanClick = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    event.preventDefault();
    await trackPlanSelection(plan, deal);
    window.location.href = href;
  };
  const dealAmt = Math.round(plan?.currency.origUnitAmount ?? 0);
  const planAmt = Math.round(plan?.currency.unitAmount ?? 0);

  return (
    <Box
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      width="100%"
    >
      {index === 1 && <HeartImage isActive={isActive} />}
      <A
        href={href}
        onClick={onPlanClick}
        data-testid="plan"
        data-plan-code={plan.code}
        display="block"
        sx={{
          zIndex: 0,
          borderRadius: 5,
          transition: 'all 0.2s ease-in-out',
          boxShadow: 'large',
          cursor: 'pointer',
          textDecoration: 'none',
          ':hover': {
            transform: 'translateY(-8px)',
          },
          ':active': {
            transform: 'translateY(-8px)',
          },
          position: 'relative',
        }}
        backgroundColor="white"
        overflow="hidden"
        marginBottom="20px"
        width="100%"
      >
        <Box>
          <Flex
            backgroundColor={index === 1 ? colors.red : colors.gray10}
            fontSize={20}
            textAlign="center"
            sx={{
              flexDirection: 'column',
              minHeight: '68px',
              justifyContent: 'center',
            }}
          >
            <H2
              marginRight="1"
              opacity=".8"
              color={textColor}
              data-testid="plan-title"
            >
              {intl.formatMessage({
                id: title,
                defaultMessage: title,
              })}
            </H2>
            {subtitle && (
              <P
                fontSize={1}
                mt="4px"
                fontWeight="bold"
                color={textColor}
                data-testid="plan-subtitle"
              >
                {subtitle}
              </P>
            )}
          </Flex>
          <Flex
            flexDirection="column"
            justifyContent="flex-end"
            backgroundColor="white"
            sx={{ alignItems: 'center', padding: '36px 20px' }}
          >
            <Box pb="16px">
              <Box textAlign="center">
                {/* strikeout price */}
                {isDeal && reducedPrice > 0 && (
                  <P
                    sx={{ textDecoration: 'line-through' }}
                    color="negative"
                    fontWeight="bold"
                    data-testid="plan-currency-base"
                  >
                    {getCurrencySymbol(plan.currency.currency)}
                    {formatPrice(dealAmt || planAmt || 0)}
                  </P>
                )}
                {/* reduced price */}
                <Flex
                  justifyContent="center"
                  sx={{
                    color: 'gray60',
                    fontSize: '36px',
                  }}
                >
                  <P
                    ml="-1"
                    fontSize={18}
                    fontWeight="700"
                    data-testid="plan-currency-symbol"
                  >
                    {getCurrencySymbol(plan.currency.currency)}
                  </P>
                  <Flex>
                    <P fontSize={36} fontWeight="700" data-testid="plan-price">
                      {!isDeal
                        ? formatPrice(planAmt)
                        : formatPrice(reducedPrice)}
                    </P>
                  </Flex>
                </Flex>
              </Box>
            </Box>
            <Box textAlign="center">
              <Button
                data-testid="plan-buy-button"
                color={index === 1 ? colors.white : colors.gray15}
                fontSize={16}
                backgroundColor={index === 1 ? colors.red : colors.white}
                alignItems="center"
                justifyContent="center"
                px={4}
                py={2}
                sx={{
                  boxShadow:
                    index === 1
                      ? `0 0 0 1px ${colors.red} inset`
                      : '0 0 0 1px #c9c9c9 inset',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  outline: 0,
                  borderRadius: 160,
                  transition: 'all 0.1s ease-in-out 0s',
                  ':hover': {
                    backgroundColor: `${
                      index === 1 ? darken(0.1, colors.red) : colors.gray5
                    }`,
                  },
                }}
              >
                <FormattedMessage
                  id="plans.selection.buy"
                  defaultMessage="Buy now"
                />
              </Button>
              {plan.autoRenew ? (
                <Box mt="12px">
                  <P
                    fontSize={14}
                    color="gray40"
                    data-testid="plan-auto-renew-period"
                  >
                    {intl.formatMessage(renewalTitle)}
                  </P>
                </Box>
              ) : null}
            </Box>
          </Flex>
        </Box>
      </A>
    </Box>
  );
};

const HeartImage = ({ isActive }: { isActive: boolean }) => {
  return (
    <Flex
      data-testid="heart-image"
      sx={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%) rotate(-14deg)',
        top: '-40px',
        width: '190px',
        transition: 'all 0.2s ease-in-out',
        zIndex: 0,
        transitionDelay: '0.1s',
        willChange: 'transform',
        ...(isActive && {
          transform: 'translateY(-170px) translateX(-50%)',
        }),
      }}
    >
      <Image
        src="https://d3cdo0emj8d2qc.cloudfront.net/assets/MDUQF6E8TZmZltZhuhBWHhQtTNaGdq-g.png"
        alt=""
        width="100%"
        sx={{
          display: ['none', 'none', 'block'],
        }}
      />
    </Flex>
  );
};
